import React, {useState, useEffect} from 'react';
import {
    Container,
    Typography,
    Grid,
    Box,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import {CourseSearchCard} from "./CourseSearchCard.jsx";
import {createApiClient} from '../../services/ApiClient.jsx';


export const CourseSearch = ({accessToken, onCourseEnrolled}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setCourseSearchResults] = useState([]);
    const [enrolledCourses, setEnrolledCourses] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [joiningCourseId, setJoiningCourseId] = useState(null);
    const [showConfetti, setShowConfetti] = useState(null);

    const apiClient = createApiClient(accessToken);

    const fetchEnrolledCourses = async () => {
        try {
            const response = await apiClient.get('/api/courses/enrolled');
            setEnrolledCourses(response.courses || []);
        } catch (err) {
            console.error('Error fetching enrolled courses:', err);
            // Don't show error for this, as it's not critical for search functionality
        }
    };

    const searchCourses = async (query = '') => {
        try {
            setSearchLoading(true);
            setSearchError(null);

            let response;
            if (query.trim()) {
                response = await apiClient.get(`/api/courses/search?q=${encodeURIComponent(query)}&limit=10`);
            } else {
                response = await apiClient.get('/api/courses/?limit=10');
            }

            setCourseSearchResults(response.courses || []);
            setHasSearched(true);
        } catch (err) {
            console.error('Error searching courses:', err);
            setSearchError('Failed to search courses. Please try again later.');
            setCourseSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        // Load initial courses and enrolled courses when component mounts
        const initializeData = async () => {
            await fetchEnrolledCourses();
            await searchCourses();
        };
        initializeData();
    }, []);

    const handleSearch = () => {
        searchCourses(searchQuery);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        searchCourses('');
    };

    const handleJoinCourse = async (course) => {
        try {
            setJoiningCourseId(course.id);
            setSearchError(null);

            // Use the new self-enrollment endpoint
            const response = await apiClient.post(`/api/courses/${course.id}/join`);

            // Wait 500ms to show the loading animation before confetti
            setTimeout(() => {
                // Clear the joining state and show confetti explosion
                setJoiningCourseId(null);
                setShowConfetti(course.id);

                // Hide confetti after 3 seconds
                setTimeout(() => {
                    setShowConfetti(null);
                }, 3500);

                // Refresh both enrolled courses and search results
                fetchEnrolledCourses();
                searchCourses(searchQuery);

                // Notify parent component that a course was enrolled
                if (onCourseEnrolled) {
                    onCourseEnrolled(course);
                }
            }, 500);

        } catch (err) {
            console.error('Error joining course:', err);

            // Handle different error cases
            if (err.response?.status === 403) {
                setSearchError('Unable to join course. Teachers cannot join courses as students.');
            } else if (err.response?.status === 409) {
                setSearchError('You are already enrolled in this course.');
            } else if (err.response?.status === 404) {
                setSearchError('Course not found. It may have been deleted.');
            } else {
                setSearchError('Failed to join course. Please try again later.');
            }

            // Clear joining state immediately on error
            setJoiningCourseId(null);
        }
        // Note: We don't clear joiningCourseId here in the finally block anymore
        // because we want it to stay active during the 500ms delay
    };

    // Create a Set of enrolled course IDs for quick lookup
    const enrolledCourseIds = new Set(enrolledCourses.map(course => course.id));

    return (
        <Box>
            {/* Search Bar */}
            <Box sx={{mb: 2}}>
                <TextField
                    fullWidth
                    placeholder="Search for courses by title or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action"/>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                    {searchQuery && (
                                        <IconButton onClick={handleClearSearch} size="small" sx={{mr: 1}}>
                                            <ClearIcon/>
                                        </IconButton>
                                    )}
                                    {searchLoading ? (
                                        <CircularProgress size={20} color="inherit"/>
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            // Use theme default radius
                        },
                    }}
                />
            </Box>

            {/* Results Count - Now directly under search bar */}
            {hasSearched && !searchLoading && (
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {searchQuery
                        ? `Found ${searchResults.length} course${searchResults.length !== 1 ? 's' : ''} matching "${searchQuery}"`
                        : `Showing ${searchResults.length} available course${searchResults.length !== 1 ? 's' : ''}`
                    }
                </Typography>
            )}

            {/* Search Error */}
            {searchError && (
                <Alert severity="error" sx={{mb: 3}}>
                    {searchError}
                </Alert>
            )}

            {/* Search Results */}
            {searchLoading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            ) : hasSearched ? (
                searchResults.length === 0 ? (
                    <Card>
                        <CardContent sx={{textAlign: 'center', py: 6}}>
                            <SearchIcon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}/>
                            <Typography variant="h6" gutterBottom>
                                {searchQuery ? 'No Courses Found' : 'No Courses Available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {searchQuery
                                    ? `No courses match "${searchQuery}". Try a different search term.`
                                    : 'There are no courses available to browse at the moment.'
                                }
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {searchResults.map((course) => (
                            <Grid size={{xs: 12, sm: 6, md: 4}} key={course.id}>
                                <CourseSearchCard
                                    course={course}
                                    onJoinCourse={handleJoinCourse}
                                    isJoining={joiningCourseId === course.id}
                                    isEnrolled={enrolledCourseIds.has(course.id)}
                                    showConfetti={showConfetti === course.id}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )
            ) : null}
        </Box>
    );
};